var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../../enum';
import { ErrorMessageBox, FallbackComponent, getLOBName, getUnassignedLOB, sortByLOBName } from '../../common';
import { CopyDataFrom } from '../common/copyDataFrom';
import { WizardStep } from '../constants';
import { useRefreshQueueData } from '../queue/selectors';
import { QueueStatusType } from '../queue/types';
import { useEffectiveCapacityPermission, useIsPlanLocked, useWizardParams } from '../selectors';
import { useClearPartTimeAPI, useCopyPartTimeAPI } from './selectors';
import { WorkingDaysBaseTable } from './workingDaysBaseTable';
export var PartTimeForm = function () {
  var t = useTranslation('wizard', {
    keyPrefix: 'workingDay'
  }).t;
  var capacityPermission = useEffectiveCapacityPermission();
  var isPlanLocked = useIsPlanLocked();
  var disabled = capacityPermission !== StatusPermission.Edit || isPlanLocked;
  var _a = useWizardParams(),
    step = _a.step,
    queueId = _a.queueId;
  var refreshData = useRefreshQueueData();
  var _b = useClearPartTimeAPI(),
    clearedData = _b[0],
    clearData = _b[1],
    cleanClearedData = _b[2];
  var _c = useCopyPartTimeAPI(),
    copiedData = _c[0],
    copyData = _c[1],
    clearCopiedData = _c[2];
  var isReview = step === WizardStep.Review;
  var handlePartTimeData = useCallback(function (queuesData) {
    var unassigned = getUnassignedLOB();
    return queuesData.filter(function (q) {
      return q.id !== queueId;
    }).map(function (_a) {
      var id = _a.id,
        name = _a.name,
        lobName = _a.lobName,
        subLobName = _a.subLobName;
      return {
        id: String(id),
        name: name,
        lobName: getLOBName(lobName, subLobName, unassigned)
      };
    }).sort(function (a, b) {
      return sortByLOBName(a, b, unassigned);
    });
  }, [queueId]);
  useEffect(function () {
    if (clearedData.data || copiedData.data) {
      refreshData();
    }
    return function () {
      cleanClearedData();
      clearCopiedData();
    };
  }, [cleanClearedData, clearCopiedData, clearedData.data, copiedData.data, refreshData]);
  var handleCopy = function (source) {
    copyData(source);
  };
  var handleClear = function () {
    clearData();
  };
  return _jsxs(Stack, __assign({
    paddingTop: 3,
    position: "relative"
  }, {
    children: [_jsx(ErrorMessageBox, {
      errors: [copiedData.error, clearedData.error]
    }), _jsxs(FallbackComponent, __assign({
      requesting: !!copiedData.requesting || !!clearedData.requesting,
      overlay: true
    }, {
      children: [_jsxs(Stack, __assign({
        direction: "row",
        justifyContent: "space-between",
        gap: 3
      }, {
        children: [_jsxs(Box, __assign({
          width: {
            xs: '50%',
            md: '75%'
          }
        }, {
          children: [_jsx(Typography, __assign({
            variant: "subtitle1",
            gutterBottom: true,
            sx: {
              mb: 2
            }
          }, {
            children: t('partTime.title')
          })), !isReview && _jsx(Typography, __assign({
            variant: "body1",
            gutterBottom: true,
            paddingBottom: 2
          }, {
            children: t('partTime.description')
          }))]
        })), !isReview && _jsx(CopyDataFrom, {
          statusType: QueueStatusType.WorkingDays,
          onCopy: handleCopy,
          onClear: handleClear,
          needSaveBeforeCopy: true,
          disabled: disabled,
          getOptions: handlePartTimeData
        })]
      })), _jsx(WorkingDaysBaseTable, {
        disabled: disabled
      })]
    }))]
  }));
};